import { Badge } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { AG_GRID_LOCALE_NOR } from "../../../../agGridLocale";
import { formatDate, formatShortDate, isDateValidFrontend } from "../../admin/selectors";
import { useGetProjectByProjectIdChecklistAndChecklistIdLogsRowIdQuery } from "../../../../../openapi";
import {
  ChecklistModalHistoryLog,
  selectChecklistModalLogsArgs,
  selectChecklistModalLogsViewModel,
} from "../checklistSelectors";
import { useAppSelector } from "../../../../hooks";

const isEmpty = (value: unknown) => value == null || (typeof value === "string" && value.trim() === "");

const formatValue = (value: any) => {
  if (isEmpty(value)) {
    return "";
  }

  if (isDateValidFrontend(value)) {
    return formatDate(value);
  }

  try {
    if (value.startsWith("[")) {
      const parsedValue = JSON.parse(value);
      if (parsedValue.constructor === Array) {
        return (
          <div className="d-flex flex-wrap justify-content-start">
            {parsedValue.map((tag, index) => (
              <Badge key={`${tag}-${index}`} className="greybadge" size="sm" radius="4px" fz="11px">
                {tag}
              </Badge>
            ))}
          </div>
        );
      }
    }
  } catch (error) {
    console.warn(error);
  }

  return value;
};

const getColumns = (hasUsername: boolean, hasUserDotName: boolean): ColDef<ChecklistModalHistoryLog>[] => [
  {
    field: "columnDisplayName",
    headerName: "Kolonne",
    filter: "agSetColumnFilter",
    width: 180,
    comparator: (a, b) => String(a || "").localeCompare(String(b || "")),
  },
  { field: "_id", hide: true },
  {
    field: "newValue",
    headerName: "Ny verdi",
    flex: 1,
    cellStyle: { padding: "6px 0px" },
    filter: "agTextColumnFilter",
    cellRenderer: (params) => formatValue(params.value),
  },
  {
    field: "oldValue",
    headerName: "Forrige verdi",
    flex: 1,
    cellStyle: { padding: "6px 0px" },
    filter: "agTextColumnFilter",
    cellRenderer: (params) => formatValue(params.value),
  },
  {
    field: "userName",
    headerName: "Redigert av",
    filter: "agTextColumnFilter",
    width: 160,
    hide: !hasUsername,
  },
  {
    field: "user.name",
    headerName: "Redigert av",
    filter: "agTextColumnFilter",
    width: 160,
    hide: !hasUserDotName,
  },
  {
    field: "updatedAt",
    headerName: "Endret dato",
    width: 132,
    sort: "desc",
    valueFormatter: (params) => (isDateValidFrontend(params.value) ? formatShortDate(params.value) : ""),
    comparator: (a, b) => {
      const da = isDateValidFrontend(a) ? new Date(a).getTime() : 0;
      const db = isDateValidFrontend(b) ? new Date(b).getTime() : 0;
      return da - db;
    },
  },
];

const ChecklistModalLogsRefactored: React.FC = () => {
  const { checklistId } = useParams<{ checklistId: string }>();
  const location = useLocation();
  const checklistRowId = new URLSearchParams(location.search).get("id");
  const logsArgs = useAppSelector((state) => selectChecklistModalLogsArgs(state, checklistId, checklistRowId));
  useGetProjectByProjectIdChecklistAndChecklistIdLogsRowIdQuery(logsArgs ?? skipToken);
  const { hasUsername, hasUserDotName, rowData } = useAppSelector((state) =>
    selectChecklistModalLogsViewModel(state, checklistId, checklistRowId),
  );
  const columns = useMemo(() => getColumns(hasUsername, hasUserDotName), [hasUsername, hasUserDotName]);

  return (
    <div className="ag-theme-quartz h-100 w-100">
      <AgGridReact
        domLayout="autoHeight"
        rowData={rowData}
        columnDefs={columns}
        defaultColDef={{
          suppressHeaderMenuButton: true,
          autoHeight: true,
          minWidth: 120,
          maxWidth: 500,
          cellStyle: {
            overflow: "hidden",
            whiteSpace: "normal",
            lineHeight: "1.5",
            wordBreak: "break-word",
          },
        }}
        rowHeight={30}
        headerHeight={35}
        suppressClickEdit
        animateRows={false}
        suppressContextMenu
        localeText={AG_GRID_LOCALE_NOR}
        getRowId={(params) => params.data._id}
      />
    </div>
  );
};

export default ChecklistModalLogsRefactored;
